import { supabase } from '../lib/supabaseClient';
import { ApiErrorConCodigo, esErrorPermanente } from '../lib/apiErrors';
import { setSoftysUploader } from '../offline/softysQueue';
import { completarTrabajadores } from './trabajadoresDeMisTiendas';
import {
  CONFIG_TOALLITAS, leerConfigVisitasPuesto, ROTULOS_DE_SIEMPRE, rotulosDeLasCampanias,
  type ConfigVisitasPuesto, type RotulosVisitasPuesto,
} from '../domain/softysConfig';

/**
 * Softys · Toallitas Húmedas (Babysec) — las visitas a puestos de mercado.
 *
 * La campaña trabaja **un mercado por día**, con una persona registrada por mercado que recorre
 * los puestos uno por uno. En cada puesto hace una de dos cosas, y puede alternarlas libremente:
 *
 * · **Visita a comerciante** — la planificada: se le habla de la marca, se lo capacita y se ve si
 *   hace pedido. Mide al COMERCIANTE, o sea sell-in.
 * · **Visita de ventas** — la del canje: alguien compra promociones de toallitas, gira la ruleta y
 *   se lleva merch. Mide a quien COMPRA, o sea sell-out.
 *
 * Son dos tablas y no una con un campo «tipo» justamente por eso: son dos mediciones distintas, y
 * el día que Softys pregunte cuánto se vendió al consumidor la respuesta no puede venir mezclada
 * con los pedidos de los comerciantes.
 *
 * **Lo que NO se pregunta, porque el sistema ya lo sabe:** quién registra (sale de la sesión), en
 * qué mercado está y de qué turno viene (salen de la asignación), la fecha, y la hora de la visita
 * —que se sella sola: una hora tecleada treinta veces por jornada sale mal la mitad de las veces—.
 * Tampoco la selfie de la dupla: esa es la foto del marcado de asistencia y ya se guarda ahí.
 */

/** Los cuatro premios de la ruleta de Toallitas. Las otras campañas traen los suyos en
 *  `campaigns.visitas_puesto_config` (ver `cargarConfigVisitasPuesto`). */
export const PREMIOS_RULETA = CONFIG_TOALLITAS.premios;

/** Cómo viene el pedido del comerciante. Lo acepta igual el CHECK de la tabla. */
export const UNIDADES_PEDIDO = ['planchas', 'unidades'] as const;
export type UnidadPedido = (typeof UNIDADES_PEDIDO)[number];

export type TipoVisitaSoftys = 'comerciante' | 'venta';

/** Error con su codigo, para que la cola distinga «no hay red» de «el servidor lo rechazo».
 *  Es la distincion que decide si el reintento gasta un intento o no: una jornada sin senal no
 *  puede terminar con las visitas marcadas como fallidas. */
export class SoftysApiError extends ApiErrorConCodigo {
  constructor(message: string, code: string | null = null) {
    super(message, code, 'SoftysApiError');
  }
}

/** De dónde salen la tienda, el turno y el día: de la asignación, no de una pregunta. */
export interface ContextoVisitaSoftys {
  /** `null` cuando no había programación; el registro queda igual, sin turno al que colgarse. */
  assignmentId: string | null;
  pdvId: string;
  fecha: string;
}

export interface VisitaSoftysInput {
  clientUuid: string;
  tipo: TipoVisitaSoftys;
  contexto: ContextoVisitaSoftys;
  ocurridoEn: string;

  /** El número de orden que el puesto TIENE dentro del mercado. Es texto: en campo aparecen
   *  «12B» y «A-4», y un entero los perdería. */
  puestoNumero: string;
  puestoNombre: string;
  /** DNI del COMERCIANTE. Dato de un tercero: viaja a su columna y nunca al jsonb. */
  comercianteDni: string | null;

  fotoFachadaPath: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;

  /**
   * Banderas de calidad del dato, calculadas en la pantalla.
   *
   * 🔴 Existían como columna desde la 0146 y **nadie las llenaba**: las 549 visitas de los tres
   * primeros días de campaña llegaron con el arreglo vacío. Una columna de calidad que nunca se
   * escribe es peor que no tenerla, porque la bandeja la muestra en blanco y eso se lee como
   * «no hay nada que revisar».
   */
  flags?: string[];

  /**
   * `false` cuando la persona eligió OTRA fecha en el formulario (pedido del usuario, 02-nov): lo
   * que se cargó después no tiene la hora del puesto, tiene una hora puesta. Es la misma convención
   * del canje manual de Vital, y la bandeja ya la muestra como «hora aproximada».
   * Sin la clave = `true`: así se comportan las visitas que ya estaban en la cola.
   */
  horaReal?: boolean;

  /** Un giro por visita y venta cerrada (decisión del usuario, 13-set). Texto: cada campaña
   *  trae su lista de premios. */
  premio: string | null;

  // --- solo «comerciante» -------------------------------------------------
  hizoPedido?: boolean | null;
  pedidoUnidad?: UnidadPedido | null;
  pedidoCantidad?: number | null;
  fotoPedidoPath?: string | null;
  escuchoCapacitacion?: boolean | null;
  fotoSalidaPath?: string | null;
  /** Solo si la campaña lo pregunta (Ladysoft, 0334). Van a `ficha`: `undefined` = no se preguntó,
   *  `null` = se preguntó y quedó sin contestar. Son dos cosas distintas en un reporte. */
  muestraEntregada?: boolean | null;
  bonificacionPacks?: number | null;
  /** Con qué dinámica de la lámina compró, o «Sin dinámica». Mismo criterio: `undefined` = no se preguntó. */
  dinamicaMayorista?: string | null;

  // --- solo «venta» -------------------------------------------------------
  promociones?: number | null;
}

const BUCKET = 'photos';

/**
 * Sube una foto de la visita y devuelve su ruta.
 *
 * Va al prefijo `form-photos/`, que **ya tiene** políticas de INSERT y de UPDATE desde la 0004, y
 * de lectura para el equipo de la campaña y para el rol `datos`. Por eso este módulo no necesita
 * ninguna migración de Storage — y por eso no se usa un prefijo propio: un prefijo nuevo hay que
 * acordarse de abrirlo, y el síntoma de olvidarlo es una foto que sube bien y que después nadie
 * puede ver.
 *
 * `upsert: true` es lo que hace idempotente el reintento de la cola: misma ruta, mismo contenido.
 * Sin el UPDATE en la política, el primer reintento moriría con 42501 y la visita quedaría
 * atascada en el celular para siempre.
 */
export async function subirFotoVisitaSoftys(
  clientUuid: string,
  tipo: 'fachada' | 'pedido' | 'salida',
  foto: Blob,
): Promise<string> {
  const path = `form-photos/sof-${clientUuid}-${tipo}.jpg`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, foto, {
    contentType: 'image/jpeg',
    upsert: true,
  });
  if (error) {
    const code = (error as { statusCode?: string | number }).statusCode;
    throw new SoftysApiError(error.message, code === undefined ? null : String(code));
  }
  return path;
}

/**
 * Guarda la visita en la tabla que le corresponde.
 *
 * `worker_id` se manda explícito porque la política de escritura exige `worker_id = auth.uid()`:
 * si alguien intentara escribir a nombre de otra persona, la base lo rechaza. O sea que mandarlo
 * no es confiar en el cliente — es dejar que el servidor lo compruebe.
 *
 * ⚠️ `client_uuid` es único: reenviar la misma visita NO crea una segunda. Es lo que sostiene la
 * cola sin señal, y es la razón de que el uuid se genere en el celular ANTES del primer intento y
 * se reutilice en cada reintento.
 */
export async function registrarVisitaSoftys(input: VisitaSoftysInput): Promise<string> {
  const { data: sesion } = await supabase.auth.getUser();
  const workerId = sesion.user?.id;
  if (!workerId) throw new SoftysApiError('Tu sesión se cerró. Vuelve a entrar.', 'sin_sesion');

  const comun = {
    client_uuid: input.clientUuid,
    worker_id: workerId,
    assignment_id: input.contexto.assignmentId,
    pdv_id: input.contexto.pdvId,
    fecha: input.contexto.fecha,
    ocurrido_en: input.ocurridoEn,
    puesto_numero: input.puestoNumero.trim() || null,
    puesto_nombre: input.puestoNombre.trim() || null,
    comerciante_dni: input.comercianteDni?.trim() || null,
    foto_fachada: input.fotoFachadaPath,
    lat: input.lat,
    lng: input.lng,
    accuracy: input.accuracy,
    premio: input.premio,
    flags: input.flags ?? [],
    hora_real: input.horaReal ?? true,
    /**
     * 🔴 Estaba cableado a `'online'`, así que las 549 visitas de los tres primeros días dicen
     * que se enviaron con señal — incluidas las que salieron de la cola horas después.
     *
     * Una columna que siempre dice lo mismo no es un dato: es un adorno que se lee como dato. Peor,
     * invita a la conclusión contraria a la verdad — «cero problemas de cobertura» sobre una
     * campaña que trabaja de Iquitos a Tacna.
     *
     * Se lee al ENVIAR, que es la misma convención de los formularios de P&G.
     */
    origin: typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'online',
  };

  const tabla = input.tipo === 'comerciante' ? 'sof_visitas' : 'sof_canjes';
  const fila = input.tipo === 'comerciante'
    ? {
      ...comun,
      hizo_pedido: input.hizoPedido ?? null,
      pedido_unidad: input.pedidoUnidad ?? null,
      pedido_cantidad: input.pedidoCantidad ?? null,
      foto_pedido: input.fotoPedidoPath ?? null,
      escucho_capacitacion: input.escuchoCapacitacion ?? null,
      foto_salida: input.fotoSalidaPath ?? null,
      ficha: fichaComerciante(input),
    }
    : { ...comun, promociones: input.promociones ?? null };

  const { data, error } = await supabase.from(tabla)
    .upsert(fila, { onConflict: 'client_uuid', ignoreDuplicates: true })
    .select('id');

  if (error) throw new SoftysApiError(error.message, error.code ?? null);

  // Si fue duplicado no vuelve fila: se busca la que ya estaba. Un reintento tiene que terminar
  // en «guardado», no en un error que mande a registrar el puesto por segunda vez.
  const id = data?.[0]?.id as string | undefined;
  if (id) return id;
  const ya = await supabase.from(tabla).select('id').eq('client_uuid', input.clientUuid)
    .maybeSingle();
  return (ya.data?.id as string | undefined) ?? input.clientUuid;
}

/** Las respuestas que no tienen columna. Solo viaja lo que la campaña preguntó: una clave ausente
 *  dice «no se preguntó», y no se puede confundir con un «no» ni con un vacío. */
function fichaComerciante(input: VisitaSoftysInput): Record<string, unknown> {
  const ficha: Record<string, unknown> = {};
  if (input.muestraEntregada !== undefined) ficha.muestra_entregada = input.muestraEntregada;
  if (input.bonificacionPacks !== undefined) ficha.bonificacion_packs = input.bonificacionPacks;
  if (input.dinamicaMayorista !== undefined) ficha.dinamica = input.dinamicaMayorista;
  return ficha;
}

/** Lee del jsonb lo que `fichaComerciante` escribió. Lo que no está vuelve como `null`. */
function deLaFicha(ficha: unknown): {
  muestraEntregada: boolean | null; bonificacionPacks: number | null; dinamicaMayorista: string | null;
} {
  const f = (ficha ?? {}) as Record<string, unknown>;
  return {
    muestraEntregada: typeof f.muestra_entregada === 'boolean' ? f.muestra_entregada : null,
    bonificacionPacks: typeof f.bonificacion_packs === 'number' ? f.bonificacion_packs : null,
    dinamicaMayorista: typeof f.dinamica === 'string' ? f.dinamica : null,
  };
}

/**
 * Qué pregunta y qué premios lleva la pantalla de puestos en ESTA campaña (0334).
 *
 * ⚠️ Un error de lectura LANZA, no devuelve la configuración de Toallitas. Caer a la de Toallitas
 * le mostraría a una impulsadora de Ladysoft otros premios sin un solo aviso, y lo que eligiera
 * llegaría a la bandeja como si fuera dato. Mejor una pantalla que dice «no se pudo cargar».
 */
/**
 * Los nombres de los dos botones de «Mi turno» para las campañas de estos turnos.
 *
 * Al revés que los premios, acá un error NO lanza: son nombres de botones, y con los de siempre la persona igual
 * puede registrar. Una pantalla de «Mi turno» que no carga por un rótulo dejaría a todo el mundo sin marcar.
 */
export async function rotulosVisitasPuesto(campaignIds: readonly string[]): Promise<RotulosVisitasPuesto> {
  const ids = [...new Set(campaignIds.filter(Boolean))];
  if (ids.length === 0) return ROTULOS_DE_SIEMPRE;
  try {
    const { data, error } = await supabase.from('campaigns')
      .select('visitas_puesto_config').in('id', ids).eq('visitas_puesto', true);
    if (error) return ROTULOS_DE_SIEMPRE;
    return rotulosDeLasCampanias(((data ?? []) as { visitas_puesto_config: unknown }[])
      .map((c) => leerConfigVisitasPuesto(c.visitas_puesto_config ?? null)));
  } catch {
    return ROTULOS_DE_SIEMPRE;
  }
}

export async function cargarConfigVisitasPuesto(campaignId: string): Promise<ConfigVisitasPuesto> {
  const { data, error } = await supabase.from('campaigns')
    .select('visitas_puesto_config').eq('id', campaignId).maybeSingle();
  if (error) throw new SoftysApiError(error.message, error.code ?? null);
  return leerConfigVisitasPuesto(
    (data as { visitas_puesto_config?: unknown } | null)?.visitas_puesto_config ?? null,
  );
}

/** Una visita ya registrada, para que la persona vea lo que lleva del día. */
export interface VisitaSoftysDelDia {
  id: string;
  tipo: TipoVisitaSoftys;
  puestoNumero: string | null;
  puestoNombre: string | null;
  premio: string | null;
  ocurridoEn: string;
  /** Lo de abajo existe para que la persona pueda COMPROBAR lo que subió, no solo contarlo.
   *  Sin esto la lista dice «registraste el puesto 12» y no hay forma de ver si el pedido quedó
   *  en 5 planchas o en 50, que es justo lo que alguien quiere revisar antes de seguir. */
  comercianteDni: string | null;
  hizoPedido: boolean | null;
  pedidoUnidad: string | null;
  pedidoCantidad: number | null;
  escuchoCapacitacion: boolean | null;
  muestraEntregada: boolean | null;
  bonificacionPacks: number | null;
  dinamicaMayorista: string | null;
  promociones: number | null;
  tieneFotoFachada: boolean;
  /** `false` = se cargó después, para otra fecha: la hora de `ocurridoEn` no es la del puesto. */
  horaReal?: boolean;
  /** `true` cuando todavía está en el teléfono esperando señal: no llegó al servidor. */
  pendiente?: boolean;
}

/**
 * Lo que esta persona ya registró hoy, de los dos instrumentos, más reciente primero.
 *
 * Existe porque sin esto, en un mercado de treinta puestos, no hay forma de saber si el puesto 12
 * ya entró — y el único remedio en campo es registrarlo de nuevo. Escribir sin poder leer es como
 * no haber escrito.
 */
export async function misVisitasSoftysDelDia(fecha: string): Promise<VisitaSoftysDelDia[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const uid = sesion.user?.id;
  if (!uid) return [];

  const comunes = 'id, puesto_numero, puesto_nombre, premio, ocurrido_en, comerciante_dni,'
    + ' foto_fachada, hora_real';
  const [v, c] = await Promise.all([
    supabase.from('sof_visitas')
      .select(comunes + ', hizo_pedido, pedido_unidad, pedido_cantidad, escucho_capacitacion, ficha')
      .eq('worker_id', uid).eq('fecha', fecha),
    supabase.from('sof_canjes').select(comunes + ', promociones')
      .eq('worker_id', uid).eq('fecha', fecha),
  ]);
  if (v.error) throw new SoftysApiError(v.error.message, v.error.code ?? null);
  if (c.error) throw new SoftysApiError(c.error.message, c.error.code ?? null);

  type Fila = {
    id: string; puesto_numero: string | null; puesto_nombre: string | null;
    premio: string | null; ocurrido_en: string; comerciante_dni: string | null;
    foto_fachada: string | null; hora_real?: boolean | null;
    hizo_pedido?: boolean | null; pedido_unidad?: string | null;
    pedido_cantidad?: number | null; escucho_capacitacion?: boolean | null;
    promociones?: number | null; ficha?: unknown;
  };
  const arma = (filas: Fila[], tipo: TipoVisitaSoftys): VisitaSoftysDelDia[] =>
    filas.map((r) => ({
      id: r.id,
      tipo,
      puestoNumero: r.puesto_numero,
      puestoNombre: r.puesto_nombre,
      premio: r.premio,
      ocurridoEn: r.ocurrido_en,
      comercianteDni: r.comerciante_dni,
      hizoPedido: r.hizo_pedido ?? null,
      pedidoUnidad: r.pedido_unidad ?? null,
      pedidoCantidad: r.pedido_cantidad ?? null,
      escuchoCapacitacion: r.escucho_capacitacion ?? null,
      ...deLaFicha(r.ficha),
      promociones: r.promociones ?? null,
      tieneFotoFachada: r.foto_fachada !== null,
      horaReal: r.hora_real ?? true,
    }));

  return [
    ...arma((v.data ?? []) as unknown as Fila[], 'comerciante'),
    ...arma((c.data ?? []) as unknown as Fila[], 'venta'),
  ].sort((a, b) => b.ocurridoEn.localeCompare(a.ocurridoEn));
}

/* ══════════════════════════════════════════════════════════════════════════
   Lo que ve la OFICINA en Registros de campo
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Una jornada programada de la campaña: un mercado, un día, y lo que se registró ahí.
 *
 * 🔴 La lista sale de la PROGRAMACIÓN, no de lo registrado. Es el pedido del usuario: *«si mañana
 * tenemos diez mercados, tenemos que tener los espacios de los diez mercados ahí, para que se vaya
 * completando»*. Una bandeja que solo lista lo que llegó sirve para consultar, no para perseguir:
 * el hueco, que es lo único accionable, queda invisible.
 *
 * Es exactamente lo que pasó con Atrapa tu Bienestar el 12-set: siete tiendas terminaron el día
 * sin una sola jugada y no se vieron hasta que alguien cruzó la programación contra lo registrado.
 */
export interface JornadaSoftys {
  positionId: string;
  assignmentId: string | null;
  pdvId: string;
  mercado: string;
  fecha: string;
  /** Quién estaba asignada. `null` = la posición quedó abierta, que también es un hueco. */
  persona: string | null;
  visitasComerciante: number;
  visitasVenta: number;
  /** Cuántas de las dos llevan premio de ruleta anotado. */
  premios: number;
}

/**
 * Las jornadas de la campaña en un rango, con lo registrado en cada una.
 *
 * ⚠️ PostgREST corta en 1000 filas sin avisar. Con una posición por mercado y día esto no llega
 * ni cerca, pero el `range` explícito está puesto para que el día que llegue se note.
 */
export async function jornadasSoftys(
  campaignId: string, desde: string, hasta: string,
): Promise<JornadaSoftys[]> {
  const pos = await supabase
    .from('positions')
    .select(
      'id, date, pdv_id, pdv:pdv_id (name), '
      + 'assignments (id, status, worker_id, worker:worker_id (first_names, last_names))',
    )
    .eq('campaign_id', campaignId)
    .gte('date', desde)
    .lte('date', hasta)
    .order('date')
    .range(0, 999);
  if (pos.error) throw new SoftysApiError(pos.error.message, pos.error.code ?? null);

  const filas = (pos.data ?? []) as unknown as {
    id: string; date: string; pdv_id: string;
    pdv: { name?: string } | null;
    assignments: {
      id: string; status: string;
      worker_id: string | null;
      worker: { first_names?: string; last_names?: string } | null;
    }[] | null;
  }[];

  // El supervisor de ruta no lee `profiles` (0218): para él el embed `worker` llega en null y la
  // jornada saldría «sin asignar» aunque tenga persona. Se completa antes de mirar las asignaciones;
  // para quien sí lee `profiles` no falta ninguno y no hay llamada extra.
  await completarTrabajadores(filas.flatMap((f) => f.assignments ?? []));

  const asignaciones = filas.flatMap((f) => (f.assignments ?? [])
    .filter((a) => a.status === 'aprobada').map((a) => a.id));

  // Las visitas de todas esas jornadas, de un viaje. Se cuentan acá y no con un `count` por fila
  // para no disparar una consulta por mercado.
  // 🔴 `.eq('estado','registrado')`: una visita ANULADA no puede seguir sumando en el contador de
  // la jornada. Si sumara, anular dejaria la fila diciendo «4 puestos» con 3 adentro — la misma
  // contradiccion entre encabezado y detalle que ya se vio el 14-set y que hubo que arreglar.
  const campos = 'assignment_id, premio';
  const vacio = { data: [] as { assignment_id: string | null; premio: string | null }[], error: null };
  const [v, c] = await Promise.all([
    asignaciones.length
      ? supabase.from('sof_visitas').select(campos).eq('estado', 'registrado')
        .in('assignment_id', asignaciones)
      : Promise.resolve(vacio),
    asignaciones.length
      ? supabase.from('sof_canjes').select(campos).eq('estado', 'registrado')
        .in('assignment_id', asignaciones)
      : Promise.resolve(vacio),
  ]);
  if (v.error) throw new SoftysApiError(v.error.message, v.error.code ?? null);
  if (c.error) throw new SoftysApiError(c.error.message, c.error.code ?? null);

  type Cuenta = { comerciante: number; venta: number; premios: number };
  const por = new Map<string, Cuenta>();
  const suma = (id: string | null, cual: 'comerciante' | 'venta', premio: string | null) => {
    if (!id) return;
    const a = por.get(id) ?? { comerciante: 0, venta: 0, premios: 0 };
    a[cual] += 1;
    if (premio) a.premios += 1;
    por.set(id, a);
  };
  for (const r of (v.data ?? []) as { assignment_id: string | null; premio: string | null }[]) {
    suma(r.assignment_id, 'comerciante', r.premio);
  }
  for (const r of (c.data ?? []) as { assignment_id: string | null; premio: string | null }[]) {
    suma(r.assignment_id, 'venta', r.premio);
  }

  return filas.map((f) => {
    // ⚠️ NO se toma `assignments[0]`: es el patrón que este proyecto tiene anotado como el error
    // más caro. Se busca la APROBADA, que es la única que cuenta; las de otros estados no
    // deciden nada y tomar la primera mostraría a la persona equivocada.
    const aprobada = (f.assignments ?? []).find((a) => a.status === 'aprobada') ?? null;
    const cuenta = aprobada ? por.get(aprobada.id) : undefined;
    const w = aprobada?.worker;
    return {
      positionId: f.id,
      assignmentId: aprobada?.id ?? null,
      pdvId: f.pdv_id,
      mercado: f.pdv?.name ?? '(sin nombre)',
      fecha: f.date,
      persona: w ? `${w.first_names ?? ''} ${w.last_names ?? ''}`.trim() : null,
      visitasComerciante: cuenta?.comerciante ?? 0,
      visitasVenta: cuenta?.venta ?? 0,
      premios: cuenta?.premios ?? 0,
    };
  });
}

/** El detalle de una jornada: cada puesto registrado, de los dos instrumentos. */
export interface PuestoRegistrado {
  id: string;
  tipo: TipoVisitaSoftys;
  puestoNumero: string | null;
  puestoNombre: string | null;
  comercianteDni: string | null;
  premio: string | null;
  ocurridoEn: string;
  fotoFachada: string | null;
  hizoPedido: boolean | null;
  pedidoUnidad: string | null;
  pedidoCantidad: number | null;
  escuchoCapacitacion: boolean | null;
  /** De `ficha`: solo las campañas que lo preguntan (0334). */
  muestraEntregada: boolean | null;
  bonificacionPacks: number | null;
  dinamicaMayorista: string | null;
  fotoPedido: string | null;
  fotoSalida: string | null;
  promociones: number | null;
  /** `registrado` | `anulado`. Las anuladas se SIGUEN mostrando, tachadas: un registro que
   *  desaparece de la pantalla es indistinguible de uno que nunca existio. */
  estado: string;
  /** `online` | `offline` | `manual`. Un registro cargado a mano tiene que distinguirse para
   *  siempre, o en un mes nadie puede decir que midio el sistema y que se cargo de memoria. */
  origin: string | null;
  /** `false` = la hora se relleno al cargar a mano. Esas filas no entran a ninguna curva horaria. */
  horaReal: boolean;
}

export async function puestosDeLaJornada(assignmentId: string): Promise<PuestoRegistrado[]> {
  const [v, c] = await Promise.all([
    supabase.from('sof_visitas')
      .select('id, puesto_numero, puesto_nombre, comerciante_dni, premio, ocurrido_en, '
        + 'foto_fachada, hizo_pedido, pedido_unidad, pedido_cantidad, escucho_capacitacion, '
        + 'foto_pedido, foto_salida, estado, origin, hora_real, ficha')
      .eq('assignment_id', assignmentId),
    supabase.from('sof_canjes')
      .select('id, puesto_numero, puesto_nombre, comerciante_dni, premio, ocurrido_en, '
        + 'foto_fachada, promociones, estado, origin, hora_real')
      .eq('assignment_id', assignmentId),
  ]);
  if (v.error) throw new SoftysApiError(v.error.message, v.error.code ?? null);
  if (c.error) throw new SoftysApiError(c.error.message, c.error.code ?? null);

  type F = Record<string, unknown>;
  const arma = (filas: F[], tipo: TipoVisitaSoftys): PuestoRegistrado[] => filas.map((r) => ({
    id: r.id as string,
    tipo,
    puestoNumero: (r.puesto_numero as string | null) ?? null,
    puestoNombre: (r.puesto_nombre as string | null) ?? null,
    comercianteDni: (r.comerciante_dni as string | null) ?? null,
    premio: (r.premio as string | null) ?? null,
    ocurridoEn: r.ocurrido_en as string,
    fotoFachada: (r.foto_fachada as string | null) ?? null,
    hizoPedido: (r.hizo_pedido as boolean | null) ?? null,
    pedidoUnidad: (r.pedido_unidad as string | null) ?? null,
    pedidoCantidad: (r.pedido_cantidad as number | null) ?? null,
    escuchoCapacitacion: (r.escucho_capacitacion as boolean | null) ?? null,
    ...deLaFicha(r.ficha),
    fotoPedido: (r.foto_pedido as string | null) ?? null,
    fotoSalida: (r.foto_salida as string | null) ?? null,
    promociones: (r.promociones as number | null) ?? null,
    estado: (r.estado as string | null) ?? 'registrado',
    origin: (r.origin as string | null) ?? null,
    horaReal: (r.hora_real as boolean | null) ?? true,
  }));

  return [
    ...arma((v.data ?? []) as unknown as F[], 'comerciante'),
    ...arma((c.data ?? []) as unknown as F[], 'venta'),
  ].sort((a, b) => a.ocurridoEn.localeCompare(b.ocurridoEn));
}

/* ---------------------------------------------------------------------------
 * Corregir, anular y cargar a mano — desde la BANDEJA del panel (0170)
 *
 * Las tres son RPC y no un `update` directo, por la misma razón que en canjes: el motivo es
 * obligatorio y el rastro se escribe en la misma transacción. Un `update` desde el cliente puede
 * olvidarse del rastro; una función no.
 * ------------------------------------------------------------------------- */

/** Qué campos acepta corregir el servidor. Escrito acá para que marcar uno que no existe sea un
 *  error de compilación y no un checkbox que el servidor rechaza recién al guardar. */
export const CAMPOS_CORREGIBLES_COMERCIANTE = [
  'puesto_numero', 'puesto_nombre', 'comerciante_dni', 'hizo_pedido',
  'pedido_unidad', 'pedido_cantidad', 'escucho_capacitacion', 'premio', 'ocurrido_en',
  // En `ficha` (0353): la función los escribe ahí con el mismo rastro y el mismo motivo obligatorio.
  'dinamica', 'muestra_entregada', 'bonificacion_packs',
] as const;
export const CAMPOS_CORREGIBLES_VENTA = [
  'puesto_numero', 'puesto_nombre', 'comerciante_dni', 'promociones', 'premio', 'ocurrido_en',
] as const;
export type CampoCorregibleSoftys =
  (typeof CAMPOS_CORREGIBLES_COMERCIANTE)[number] | (typeof CAMPOS_CORREGIBLES_VENTA)[number];

/**
 * ⚠️ No están `pdv_id`, `assignment_id`, `worker_id` ni `fecha`, y no es un olvido: son los cuatro
 * que dicen a QUÉ JORNADA pertenece el registro. Moverlos convertiría una corrección en un
 * traslado, y la jornada de origen quedaría con un puesto de menos sin que nadie lo anulara. Si
 * está en la jornada equivocada, se anula y se carga de nuevo donde va — eso deja rastro en las dos.
 */
export async function corregirVisitaSoftys(
  id: string, tipo: TipoVisitaSoftys, cambios: Record<string, string | null>, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('corregir_visita_softys', {
    p_id: id, p_tipo: tipo, p_cambios: cambios, p_motivo: motivo,
  });
  if (error) throw new SoftysApiError(error.message, error.code ?? null);
}

/** Anular NO borra: marca. Un registro que desaparece de la pantalla es indistinguible de uno que
 *  nunca existió, y en un mes nadie puede explicarle al cliente por qué cambió una cifra. */
export async function anularVisitaSoftys(
  id: string, tipo: TipoVisitaSoftys, motivo: string,
): Promise<void> {
  const { error } = await supabase.rpc('anular_visita_softys', {
    p_id: id, p_tipo: tipo, p_motivo: motivo,
  });
  if (error) throw new SoftysApiError(error.message, error.code ?? null);
}

export interface AltaManualSoftys {
  assignmentId: string;
  tipo: TipoVisitaSoftys;
  puestoNumero: string;
  puestoNombre?: string | null;
  comercianteDni?: string | null;
  premio?: string | null;
  hizoPedido?: boolean | null;
  pedidoUnidad?: UnidadPedido | null;
  pedidoCantidad?: number | null;
  escuchoCapacitacion?: boolean | null;
  promociones?: number | null;
  /** `HH:mm`. Si no viene, el servidor sella el mediodía y marca la hora como inventada. */
  hora?: string | null;
  motivo: string;
}

/**
 * Carga a mano un puesto que el campo no llegó a registrar.
 *
 * ⚠️ Se cuelga de una ASIGNACIÓN, no de un mercado y una fecha sueltos: de ahí salen el PDV, el día
 * y la persona. Eso es lo que quiere decir «cuadrada a lo planificado» — un registro manual cae
 * dentro de una jornada que existe en el plan y suma en la misma fila donde se lo esperaba. Si se
 * pudieran elegir mercado y fecha a dedo, se podrían fabricar jornadas que nadie programó, y esas
 * entran al reporte del cliente.
 */
export async function altaManualVisitaSoftys(a: AltaManualSoftys): Promise<string> {
  const { data, error } = await supabase.rpc('alta_manual_visita_softys', {
    p_assignment: a.assignmentId,
    p_tipo: a.tipo,
    p_puesto_numero: a.puestoNumero,
    p_puesto_nombre: a.puestoNombre ?? null,
    p_comerciante_dni: a.comercianteDni ?? null,
    p_premio: a.premio ?? null,
    p_hizo_pedido: a.hizoPedido ?? null,
    p_pedido_unidad: a.pedidoUnidad ?? null,
    p_pedido_cantidad: a.pedidoCantidad ?? null,
    p_escucho_capacitacion: a.escuchoCapacitacion ?? null,
    p_promociones: a.promociones ?? null,
    p_hora: a.hora === undefined || a.hora === null || a.hora === '' ? null : `${a.hora}:00`,
    p_motivo: a.motivo,
  });
  if (error) throw new SoftysApiError(error.message, error.code ?? null);
  return data as string;
}

/**
 * Se registra el enviador de la cola acá abajo, igual que hacen canjes y el levantamiento.
 *
 * La cola no importa este módulo: lo recibe. Así el celular puede guardar una visita sin que la
 * capa de red esté siquiera cargada, que es justo lo que pasa dentro de un mercado sin señal.
 */
setSoftysUploader({
  subirFotoVisitaSoftys,
  registrarVisitaSoftys: (input) => registrarVisitaSoftys(input as unknown as VisitaSoftysInput),
  isPermanentError: esErrorPermanente,
});
